<template>
  <div>
    <enable-rolling-segment-dialog
      v-if="showEnableDialog"
      :segment="enableDialogSegment"
      :visible="showEnableDialog"
      @enable="confirmEnable"
      @close="closeEnableDialog"
    />
    <disable-rolling-segment-dialog
      v-if="showDisableDialog"
      :segment="disableDialogSegment"
      :campaigns="disableDialogCampaigns"
      :visible="showDisableDialog"
      :loading="disableSubmitting"
      @disable="confirmDisable"
      @close="closeDisableDialog"
    />
    <members-export-dialog v-if="showExportMembersDialog" />
    <members-export-run-dialog
      v-if="showExportMembersRunDialog"
      :task="export_result"
    />
    <members-export-add-attributes-dialog
      v-if="showExportMembersAttributesDialog"
    />
    <rest-list
      :payload="fetchEndpoint"
      ref="rolling-rest-list"
      :after-fetch="loadFinish"
    >
      <template>
        <rest-list-column
          prop="title"
          :title="$t('Audience_Segment_ListField_Title')"
          :filterable="true"
          :route="segmentOverviewRoute"
          :min-width="300"
        />
        <rest-list-column
          prop="period"
          :title="$t('Audience_Segment_RefreshInterval')"
          :filterable="false"
          :filter-input="false"
          :sortable="false"
        >
          <template v-slot="scope">
            {{ $t(rollingRefreshIntervalLabels[scope.row.period]) }}
          </template>
        </rest-list-column>
        <rest-list-column
          prop="offset"
          :title="$t('Audience_Segment_RefreshOffset')"
          :filterable="false"
          :filter-input="false"
          :sortable="false"
        >
          <template v-slot="scope">
            {{ getOffsetText(scope.row) }}
          </template>
        </rest-list-column>
        <rest-list-column
          prop="status"
          :title="$t('Audience_Segment_Status')"
          :filterable="false"
          :filter-input="false"
          :sortable="false"
          :min-width="180"
        >
          <template v-slot="scope">
            <div class="status-cell">
              <toggle
                v-if="!readonly"
                :value="isSegmentSwitchOn(scope.row)"
                :disabled="!!switchLoading[scope.row.id]"
                :active-value="1"
                :inactive-value="0"
                @change="handleSwitchChange(scope.row, $event)"
                :data-qa="`segment-toggle-${scope.row.id}`"
              />
              <span class="status-label">
                {{ getStatusLabel(scope.row) }}
              </span>
            </div>
          </template>
        </rest-list-column>
        <rest-list-column
          prop="last_sync_time"
          :title="$t('Audience_Segment_LastCalculation')"
          :filterable="false"
          :filter-input="false"
          :sortable="false"
        >
          <template v-slot="scope">
            {{ getLastCaculationText(scope.row) }}
          </template>
        </rest-list-column>

        <rest-list-column-actions v-if="!readonly">
          <template v-slot="scope">
            <rest-list-action-update :route="rowUpdateRoute(scope.row)" />
            <rest-list-action
              icon="el-icon-document-copy"
              :title="$t('RestList_Action_Copy')"
              :click="showCopyWarning"
              v-if="!isCopyAllowed"
            />
            <rest-list-action-copy v-else :route="rowCopyRoute(scope.row)" />
            <rest-list-action-delete
              :delete-message="
                $t('Audience_Segment_DeletePrompt', { label: scope.row.title })
              "
              :endpoint="rowDeleteEndpoint(scope.row)"
            />
            <rest-list-action
              v-if="$can('UserPersonalData::Export')"
              :click="actionExport"
              :row="scope.row"
              :title="$t('Audiences_Segment_Action_Export_Members')"
              icon="el-icon-download"
            />
          </template>
        </rest-list-column-actions>
      </template>
    </rest-list>
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import {
  projectEndpoint,
  projectRoute,
  projectTimezoneDate
} from '@/core/helpers'
import RestListActionCopy from '@/common/components/rest/RestListActionCopy'
import RestListActionUpdate from '@/common/components/rest/RestListActionUpdate'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListAction from '@/common/components/rest/RestListAction'
import MembersExportRunDialog from '@/modules/audiences/components/MembersExportRunDialog'
import MembersExportAddAttributesDialog from '@/modules/audiences/components/MembersExportAddAttributesDialog'
import MembersExportDialog from '@/modules/audiences/components/MembersExportDialog'
import {
  rollingRefreshIntervalLabels,
  rollingRefreshIntervalEnum
} from '@/modules/audiences/enum/RollingRefreshIntervalEnums'
import {
  segmentSyncStatus,
  isSegmentDisabled
} from '@/modules/audiences/enum/SegmentSyncStatusEnum'
import EnableRollingSegmentDialog from '@/modules/audiences/components/EnableRollingSegmentDialog'
import DisableRollingSegmentDialog from '@/modules/audiences/components/DisableRollingSegmentDialog'
import Toggle from '@/common/components/form/Toggle'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import moment from 'moment'

export default {
  components: {
    EnableRollingSegmentDialog,
    DisableRollingSegmentDialog,
    Toggle,
    MembersExportDialog,
    MembersExportAddAttributesDialog,
    MembersExportRunDialog,
    RestListColumnActions,
    RestListActionUpdate,
    RestListActionCopy,
    RestList,
    RestListColumn,
    RestListActionDelete,
    RestListAction
  },
  data() {
    return {
      taskId: null,
      showExportMembersDialog: false,
      showExportMembersRunDialog: false,
      showExportMembersAttributesDialog: false,
      exportParams: {},
      segmentToExport: {},
      export_result: {},
      rollingRefreshIntervalLabels,
      segmentSyncStatus,
      showEnableDialog: false,
      enableDialogSegment: {},
      showDisableDialog: false,
      disableDialogSegment: {},
      disableDialogCampaigns: [],
      disableSubmitting: false,
      switchLoading: {}
    }
  },
  mounted() {
    EventBus.$on('export-members-show', this.exportMembersShow)
    EventBus.$on('export-members-hide', this.exportMembersHide)
    EventBus.$on('export-members-run-show', this.exportMembersRunShow)
    EventBus.$on('export-members-run-hide', this.exportMembersRunHide)
    EventBus.$on('export-members-cancel', this.exportMembersCancel)
    EventBus.$on(
      'export-members-add-attributes-show',
      this.exportMembersAddAttributesShow
    )
    EventBus.$on(
      'export-members-add-attributes-hide',
      this.exportMembersAddAttributesHide
    )
  },
  destroyed() {
    EventBus.$off('export-members-show')
    EventBus.$off('export-members-hide')
    EventBus.$off('export-members-run-show')
    EventBus.$off('export-members-run-hide')
    EventBus.$off('export-members-add-attributes-show')
    EventBus.$off('export-members-add-attributes-hide')
    EventBus.$off('export-members-cancel')
  },
  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `segments`,
        params: {
          sort: 'title',
          type: 1,
          'per-page': 50
        }
      })
    },

    taskEndpoint() {
      return projectEndpoint({
        url: `task-histories/${this.taskId}?expand=task`
      })
    },

    readonly() {
      return !this.$can('updateProjectItem')
    },

    segmentSharing() {
      return this.$store.state.app.project.company.segment_sharing
    },

    exportEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/export`
      })
    },
    isCopyAllowed() {
      return (
        this.$refs['rolling-rest-list'].total <
        this.$store.state.app.project.max_rolling_segments_count
      )
    }
  },

  methods: {
    loadFinish() {
      EventBus.$emit(
        'get-rolling-segments-count',
        this.$refs['rolling-rest-list'].total
      )
    },
    formatDate(time, format = 'YYYY-MM-DD HH:mm:ss', hasTimezone = true) {
      if (!time) {
        return '-'
      }
      if (!hasTimezone) {
        return this.$t('CampaignsList_Label_UserTimezone', {
          date: moment
            .unix(time)
            .utc()
            .format(format)
        })
      }
      return projectTimezoneDate(time).format(format)
    },
    segmentOverviewRoute(row) {
      return projectRoute({
        name: 'AudienceRollingSegmentOverview',
        query: {
          id: row.id
        }
      })
    },
    rowUpdateRoute(row) {
      return projectRoute({
        name: 'AudienceRollingSegmentUpdate',
        query: { id: row.id }
      })
    },

    rowCopyRoute(row) {
      return projectRoute({
        name: 'AudienceRollingSegmentCopy',
        query: { id: row.id }
      })
    },

    showCopyWarning() {
      this.$message({
        message: this.$t('Audiences_RollingSegment_Max_Count_Warning', {
          count: this.$store.state.app.project.max_rolling_segments_count
        }),
        type: 'error'
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `segments/${row.id}`
      })
    },

    rowDependenciesEndpoint(row) {
      return projectEndpoint({
        method: 'get',
        url: `segments/${row.id}/dependencies`
      })
    },

    rowEnableEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `segments/${row.id}/enable`
      })
    },

    rowDisableEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `segments/${row.id}/disable`
      })
    },

    // Toggle uses activeValue=1 / inactiveValue=0 (not booleans).
    isSegmentSwitchOn(row) {
      return isSegmentDisabled(row.status) ? 0 : 1
    },

    handleSwitchChange(row, newValue) {
      const disabled = isSegmentDisabled(row.status)
      const turningOn = newValue === 1 || newValue === true

      if (turningOn && disabled) {
        this.openEnableDialog(row)
        return
      }
      if (!turningOn && !disabled) {
        this.disableSegment(row)
      }
    },

    openEnableDialog(row) {
      this.enableDialogSegment = row
      this.showEnableDialog = true
    },

    closeEnableDialog() {
      this.showEnableDialog = false
      this.enableDialogSegment = {}
    },

    confirmEnable({ full_sync }) {
      const row = this.enableDialogSegment
      this.$set(this.switchLoading, row.id, true)
      RestApi({ ...this.rowEnableEndpoint(row), data: { full_sync } })
        .then(() => {
          row.status = segmentSyncStatus.NOT_IN_PROGRESS
          this.$message({
            message: this.$t('Audience_Segment_Enabled_Success'),
            type: 'success'
          })
          this.closeEnableDialog()
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.$set(this.switchLoading, row.id, false)
        })
    },

    async disableSegment(row) {
      this.$set(this.switchLoading, row.id, true)
      try {
        const { data: deps } = await RestApi(this.rowDependenciesEndpoint(row))

        this.disableDialogSegment = row
        this.disableDialogCampaigns = (deps && deps.campaigns) || []
        this.showDisableDialog = true
      } catch (e) {
        this.$message({
          message: e.message,
          type: 'error'
        })
      } finally {
        this.$set(this.switchLoading, row.id, false)
      }
    },

    closeDisableDialog() {
      this.showDisableDialog = false
      this.disableDialogSegment = {}
      this.disableDialogCampaigns = []
    },

    async confirmDisable() {
      const row = this.disableDialogSegment
      this.disableSubmitting = true
      try {
        await RestApi(this.rowDisableEndpoint(row))

        row.status = segmentSyncStatus.MANUALLY_DISABLED

        this.$message({
          message: this.$t('Audience_Segment_Disabled_Success'),
          type: 'success'
        })
        this.closeDisableDialog()
      } catch (e) {
        this.$message({
          message: e.message,
          type: 'error'
        })
      } finally {
        this.disableSubmitting = false
      }
    },

    actionExport(segment) {
      this.segmentToExport = segment
      this.showExportMembersDialog = true
    },
    exportMembersShow() {
      this.showExportMembersDialog = true
    },

    exportMembersHide() {
      this.showExportMembersDialog = false
    },

    exportMembersCancel() {
      this.exportMembersHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    exportMembersRunShow(params) {
      Object.assign(this.exportParams, params)
      this.showExportMembersDialog = false
      this.showExportMembersRunDialog = true
      this.exportMembers()
    },

    exportMembersRunHide() {
      this.showExportMembersRunDialog = false
      this.exportParams = {}
      this.export_result = {}
      clearInterval(this.taskLoop)
      this.taskId = null
    },

    exportMembersAddAttributesShow(params) {
      this.exportParams = params
      this.showExportMembersAttributesDialog = true
    },

    exportMembersAddAttributesHide() {
      this.showExportMembersAttributesDialog = false
    },

    exportMembers() {
      let evaluateData = {
        ...this.segmentToExport,
        ...this.exportParams
      }
      RestApi.post(this.exportEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchExportTask()
          this.taskLoop = setInterval(() => {
            this.fetchExportTask()
          }, 2000)
        })
        .catch(error => {
          this.showExportMembersDialog = false
          this.showExportMembersAttributesDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchExportTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.export_result = data
      })
    },

    getStatusLabel(row) {
      if (!isSegmentDisabled(row.status)) {
        return this.$t('Audience_Segment_Status_Active')
      }

      if (Number(row.status) === segmentSyncStatus.MANUALLY_DISABLED) {
        return this.$t('Audience_Segment_Disable_Reason_Manual')
      }

      return this.$t('Audience_Segment_Disable_Reason_System')
    },

    getLastCaculationText(row) {
      if (row.last_sync_time) {
        return this.formatDate(row.last_sync_time)
      } else if (isSegmentDisabled(row.status)) {
        return this.$t('Audiences_RollingSegment_Calculation_Never')
      } else {
        return this.$t('Audiences_RollingSegment_Calculation_In_Progress')
      }
    },

    getOffsetText(row) {
      switch (row.period) {
        case rollingRefreshIntervalEnum.QUARTER_HOUR:
        case rollingRefreshIntervalEnum.HOUR:
          return this.$t('Audience_Segment_RefreshOffset_Minutes', {
            minutes: row.offset
          })
        case rollingRefreshIntervalEnum.HALF_DAY:
        case rollingRefreshIntervalEnum.DAY:
          const hours = Math.floor(row.offset / 60)
          const minutes = row.offset - hours * 60
          return (
            hours.toString().padStart(2, '0') +
            ':' +
            minutes.toString().padStart(2, '0')
          )
      }
    }
  }
}
</script>

<style scoped>
.status-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.status-label {
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/modules/audiences/pages/RollingSegmentList.vue